import { $httpFhirApi } from '@/common/api/httpFhir.service';
import { prioritySortValues } from '@/common/coverage';
import { crud } from '@/common/crud';
import { resolveServiceTimeZoneInfo, toLocalServiceDate } from '@/common/serviceTimeZone';
import { useAuthFlow } from '@/common/workflowEngine/useAuthFlow';
import { useWorkflow } from '@/common/workflowEngine/useWorkflow';
import { ChargeItem, HealthcareService, Reference } from '@/fhirworks';
import { differenceInMinutes, isValid, parseISO } from 'date-fns';
import { v4 as uuidv4 } from 'uuid';

const { postTransaction } = useWorkflow();

// Resolve claimType + revCode for a ChargeItem with the fallback chain
// fee-schedule line item (BN_FeeScheduleHealthcareService) → HealthcareService defaults.
// HealthcareService stores revenue codes as an array (revCodes[]); fee-schedule lines
// store a singular revCode that overrides the HS default when set. Issue #3433.
export function resolveClaimMeta(charge, healthcareService) {
    return {
        claimType: charge?.claimType || healthcareService?.claimType || undefined,
        revCode: charge?.revCode || healthcareService?.revCodes?.[0]?.code || undefined,
    };
}

// Decide which amount goes on the ChargeItem when a fee schedule prices a charge (#6021).
// 'contracted' (or no billing mode) bills the schedule's own amount, as before. 'standard' bills
// the charge master amount and keeps the schedule's amount on the charge as the expected
// (allowed) amount. A schedule set to 'standard' whose charge master has no price for the tier
// falls back to billing the contracted amount so the charge is never dropped or billed at $0.
export function resolveBilledAmounts({ billingMode, contractedAmount, standardAmount }) {
    if (billingMode !== 'standard') {
        return { price: contractedAmount, expectedAmount: undefined };
    }

    if (standardAmount == null) {
        console.warn('[useChargeItemFlow] standard-charge billing mode: charge master has no price for this tier, billing the contracted amount', { billingMode, contractedAmount, standardAmount });
        return { price: contractedAmount, expectedAmount: contractedAmount };
    }

    return { price: standardAmount, expectedAmount: contractedAmount };
}

// UTILITY FUNCTIONS
const processFhirBundleResponse = async function (url) {
    let result = null;
    await $httpFhirApi.get(url).then((response) => {
        if (response.data && response.data.entry && response.data.entry.length > 0) {
            result = response.data.entry.map((entry) => entry.resource);
        } else if (response.data && Array.isArray(response.data)) {
            result = response.data;
        } else if (response.data) {
            result = [response.data];
        }
    });
    return result;
};

const calculateEncounterDuration = function (encounter) {
    if (!encounter?.period?.start || !encounter?.period?.end) return 0;

    const startTime = new Date(encounter.period.start);
    const endTime = new Date(encounter.period.end);
    return differenceInMinutes(endTime, startTime);
};

const normalizeDecimalValue = function (value, fallback = 0) {
    if (value === null || value === undefined || value === '') {
        return fallback;
    }

    const numericValue = typeof value === 'number' ? value : parseFloat(value);

    return Number.isNaN(numericValue) ? fallback : numericValue;
};

export const getPerUnitChargeValue = function (charge, healthcareService, fallback = 0) {
    const rawValue = charge?.fixedTimeDuration?.perUnitCharge ?? healthcareService?.fixedTimeDuration?.perUnitCharge ?? healthcareService?.baseCharge ?? fallback;

    return normalizeDecimalValue(rawValue, fallback);
};

const calculateUnitsForDuration = function (encounterDurationMinutes, perUnitDuration, maxUnitAllowed = Infinity) {
    const durationValue = encounterDurationMinutes?.value ?? encounterDurationMinutes;

    if (durationValue <= 0 || !perUnitDuration || perUnitDuration <= 0) return 1;

    const calculatedUnits = Math.ceil(durationValue / perUnitDuration);
    const result = calculatedUnits > maxUnitAllowed ? maxUnitAllowed : calculatedUnits;

    return result;
};

const getPerUnitDurationAndMaxUnits = function (hasCoverage, charge, healthcareService) {
    let perUnitDuration;
    let maxUnitAllowed;

    // Per service has no unit duration, so it bills one unit even when a fee schedule entry
    // still carries a duration from before the charge master entry became Per service.
    if (healthcareService?.uomType === 'Service') {
        return { perUnitDuration, maxUnitAllowed };
    }

    if (hasCoverage) {
        perUnitDuration = charge?.fixedTimeDuration?.perUnitDuration;
        maxUnitAllowed = charge?.fixedTimeDuration?.maxUnitsAllowed;
    } else {
        perUnitDuration = healthcareService.fixedTimeDuration?.perUnitDuration;
        maxUnitAllowed = healthcareService.fixedTimeDuration?.maxUnitsAllowed;
    }

    return { perUnitDuration, maxUnitAllowed };
};

// Thresholds in ascending order of their minimum duration, a blank minimum counting as 0.
export const sortThresholdsByMin = (thresholds) => [...(thresholds || [])].sort((a, b) => (a.min || 0) - (b.min || 0));

export const findThresholdForDuration = function (sortedThresholds, encounterDurationMinutes) {
    if (!sortedThresholds?.length) return null;

    // Unwrap Vue computed ref if needed — matches the pattern in calculateUnitsForDuration
    const durationValue = encounterDurationMinutes?.value ?? encounterDurationMinutes ?? 0;

    if (durationValue <= 0) return null;

    // Find a threshold where duration falls between min and max
    let selectedThreshold = sortedThresholds.find((threshold) => durationValue >= (threshold.min || 0) && durationValue <= (threshold.max || Infinity));

    // Handle edge cases if no matching range found
    if (!selectedThreshold) {
        // If duration is below the minimum threshold's min value, use the first threshold
        if (durationValue < (sortedThresholds[0].min || 0)) {
            selectedThreshold = sortedThresholds[0];
        }
        // If duration exceeds the last threshold's max value, use the last threshold
        else if (durationValue > (sortedThresholds[sortedThresholds.length - 1].max || Infinity)) {
            selectedThreshold = sortedThresholds[sortedThresholds.length - 1];
        }
    }

    // If we still couldn't find a matching threshold, use the first one as fallback
    if (!selectedThreshold && sortedThresholds.length > 0) {
        selectedThreshold = sortedThresholds[0];
    }

    return selectedThreshold;
};

// The limit a session runs past when a service's suggested add-ons should be offered, or null when they shouldn't.
// A fixed-time service is judged from its own unit length and unit limit, never from units already capped at that limit,
// and never from the patient's coverage.
export const findExceededAddOnLimit = function (service, durationMinutes, selectedThreshold = null) {
    if (!((service?.suggestAddOns || service?.suggestedAddOns) && service.suggestedAddOns?.length > 0)) return null;

    const durationValue = Number(durationMinutes?.value ?? durationMinutes);

    if (service.thresholds?.length && selectedThreshold) {
        const maximumTimeAllowed = Number(selectedThreshold.maximumTimeAllowed);
        if (maximumTimeAllowed && durationValue > maximumTimeAllowed) {
            return { maximumValue: maximumTimeAllowed, limitType: 'minutes' };
        }
        return null;
    }

    // A missing, blank or 0 limit is no limit, as the charge editor reads it
    const maxUnits = Number(service.fixedTimeDuration?.maxUnitsAllowed);
    const unitDuration = Number(service.fixedTimeDuration?.perUnitDuration);
    if (!(maxUnits > 0) || !(durationValue > 0) || !(unitDuration > 0)) return null;

    // Uncapped, so the session's full length counts
    return calculateUnitsForDuration(durationValue, unitDuration) > maxUnits ? { maximumValue: maxUnits, limitType: 'units' } : null;
};

const getModifiersFromProcedure = function (procedure) {
    if (!procedure?.modifier?.length) return null;

    return procedure.modifier
        .sort((a, b) => (a.position > b.position ? 1 : a.position < b.position ? -1 : a.code > b.code ? 1 : a.code < b.code ? -1 : 0))
        .map((m) => m.code)
        .join(', ');
};

const createPatientSpecificReferences = function (references, patient, encounter) {
    return {
        ...references,
        patient: new Reference({
            id: patient.id,
            resourceType: 'Patient',
            display: patient.fullName || 'Patient',
        }),
        encounter: new Reference({
            id: encounter.id,
            resourceType: 'Encounter',
        }),
    };
};

const createCommonReferences = function (data) {
    // Create a Reference to the Patient
    const patientReference = new Reference({
        id: data.activePatient.id,
        resourceType: 'Patient',
        display:
            data.activePatient.name && data.activePatient.name[0]
                ? `${data.activePatient.name[0].given.join(' ')} ${data.activePatient.name[0].family}`
                : data.activePatient.fullName || 'Unknown Patient',
    });

    // Create a Reference to the Encounter
    const encounterReference = new Reference({
        id: data.activeEncounter.id,
        resourceType: 'Encounter',
    });

    // Create a Reference to the Provider
    const providerReference = new Reference({
        id: data.actor.id,
        resourceType: 'Practitioner',
        display: data.actor.name ? `${data.actor.name[0].given.join(' ')} ${data.actor.name[0].family}` : data.actor.fullName || 'Unknown Provider',
    });

    // QuestionnaireResponse reference if available
    let questionnaireResponseReference = null;

    // Check if questionnaire response data is available
    let questionnaireResponseData = null;

    if (data.questionnaireResponses && Array.isArray(data.questionnaireResponses) && data.questionnaireResponses.length > 0) {
        questionnaireResponseData = data.questionnaireResponses;
    } else if (data.questionnaireResponse) {
        // Handle single questionnaire response
        questionnaireResponseData = [data.questionnaireResponse];
    }

    if (questionnaireResponseData && questionnaireResponseData.length > 0) {
        const qr = questionnaireResponseData[0];
        questionnaireResponseReference = new Reference({
            id: qr.id,
            resourceType: 'QuestionnaireResponse',
        });
    }

    const references = {
        patient: patientReference,
        encounter: encounterReference,
        provider: providerReference,
        questionnaireResponse: questionnaireResponseReference,
    };

    return references;
};

// The coverage that prices a session dated serviceDate ('YYYY-MM-DD') and that its ChargeItems reference, or null.
// Candidates are active coverages and cancelled ones with a termination date; a draft (a form entry staff haven't
// confirmed), one entered in error, and one cancelled without an end date never count. Both period bounds are
// inclusive and a missing one is open. The best priority wins, then the latest effective date, then the most recently
// created, then the greatest id, so the pick doesn't depend on the order the server returns coverages in.
export function selectCoverageForServiceDate(coverages, serviceDate) {
    if (!serviceDate || !coverages?.length) return null;

    const datePart = (value) => value?.slice(0, 10);
    // Primary, Secondary, Tertiary, Quaternary, Unknown; a coverage with no priority ranks after all of them
    const priorityRank = (coverage) => prioritySortValues[coverage.insurancePriority?.coding?.[0]?.code] ?? Infinity;
    const createdAt = (coverage) => {
        const time = Date.parse(coverage.meta?.createdAt);
        return Number.isNaN(time) ? -Infinity : time;
    };
    const ranksAhead = (a, b) => {
        if (priorityRank(a) !== priorityRank(b)) return priorityRank(a) < priorityRank(b);
        const startA = datePart(a.period?.start) || '';
        const startB = datePart(b.period?.start) || '';
        if (startA !== startB) return startA > startB;
        if (createdAt(a) !== createdAt(b)) return createdAt(a) > createdAt(b);
        return (a.id ?? '') > (b.id ?? '');
    };

    return coverages
        .filter((coverage) => {
            const start = datePart(coverage.period?.start);
            const end = datePart(coverage.period?.end);
            const isCandidate = coverage.status === 'active' || (coverage.status === 'cancelled' && !!end);
            return isCandidate && (!start || start <= serviceDate) && (!end || serviceDate <= end);
        })
        .reduce((best, coverage) => (!best || ranksAhead(coverage, best) ? coverage : best), null);
}

// DATA FETCHING FUNCTIONS
// A Coverage search answers as a Bundle, a plain array or one resource depending on the client path.
const coverageListFrom = (response) => {
    if (response.data?.entry?.length > 0) return response.data.entry.map((entry) => entry.resource);
    if (Array.isArray(response.data)) return response.data;
    return response.data ? [response.data] : [];
};

const isPrimaryCoverage = (coverage) => coverage?.insurancePriority?.coding?.[0]?.code === 'P';

/**
 * A patient's active coverages that name a payer, primary first, for choosing which one a charge bills.
 * @param {string} patientId
 * @returns {Promise<object[]>}
 */
export const fetchActivePatientCoverages = async function (patientId) {
    if (!patientId) return [];
    const response = await $httpFhirApi.get(`Coverage?beneficiary=${patientId}&status=active`);
    return coverageListFrom(response)
        .filter((coverage) => coverage?.beneficiary?.id === patientId && coverage.payor?.length)
        .sort((a, b) => Number(isPrimaryCoverage(b)) - Number(isPrimaryCoverage(a)));
};

const fetchPatientCoverage = async function (patientIds, serviceDateByPatient) {
    const ids = Array.isArray(patientIds) ? patientIds : [patientIds];
    const validIds = ids.filter(Boolean);
    const resultMap = new Map();

    if (!validIds.length) {
        return resultMap;
    }

    // Without service dates (manual charge entry with no coverage chosen), each patient's primary active coverage,
    // else their first active one
    if (!serviceDateByPatient) {
        const activeCoverages = coverageListFrom(await $httpFhirApi.get(`Coverage?beneficiary=${validIds.join(',')}&status=active`));
        for (const id of validIds) {
            const patientCoverages = activeCoverages.filter((c) => c.beneficiary?.id === id);
            resultMap.set(id, patientCoverages.find(isPrimaryCoverage) || patientCoverages[0] || null);
        }
        return resultMap;
    }

    // Cancelled coverages are candidates too: one that has since ended still covers a session dated inside its period.
    // They accumulate, and a group lock asks for every participant at once, so ask for more than the server's default page.
    // Past 1,000 between them, only the first page is read.
    const coverages = coverageListFrom(await $httpFhirApi.get(`Coverage?beneficiary=${validIds.join(',')}&status=active,cancelled&_count=1000`));

    for (const id of validIds) {
        const patientCoverages = coverages.filter((c) => c.beneficiary?.id === id);
        resultMap.set(id, selectCoverageForServiceDate(patientCoverages, serviceDateByPatient.get(id)));
    }

    return resultMap;
};

// Each patient's date of service: the facility-local calendar date of the stored period.start of the encounter their
// charges attach to. The stored copy is read because endEncounter can repair a missing start without copying it back
// onto the in-memory encounter. A missing or invalid start falls back to the end, then to the lock time; an encounter
// the read doesn't return uses its in-memory period the same way.
const fetchServiceDates = async function (data, patientIds) {
    const chargeContextEncounter = (patientId) => (shouldProcessGroupEncounter(data) ? findGroupChargeEncounter(data, patientId) : data.activeEncounter);
    const encounterByPatient = new Map(patientIds.map((id) => [id, chargeContextEncounter(id)]));
    const encounterIds = [...new Set([...encounterByPatient.values()].map((encounter) => encounter?.id).filter(Boolean))];

    const storedEncounters = new Map();
    const locations = new Map();
    if (encounterIds.length) {
        const resources = await processFhirBundleResponse(`Encounter?_id=${encounterIds.join(',')}&_include=location`);
        for (const resource of resources || []) {
            if (resource.resourceType === 'Encounter') storedEncounters.set(resource.id, resource);
            if (resource.resourceType === 'Location') locations.set(resource.id, resource);
        }
    }

    // Checked before conversion: toLocalServiceDate returns input it can't convert unchanged
    const isValidInstant = (value) => isValid(parseISO(value));
    const lockTime = new Date().toISOString();
    const serviceDates = new Map();
    for (const [patientId, inMemoryEncounter] of encounterByPatient) {
        const encounter = storedEncounters.get(inMemoryEncounter?.id) || inMemoryEncounter;
        const instant = [encounter?.period?.start, encounter?.period?.end].find(isValidInstant) || lockTime;
        const location = locations.get(encounter?.location?.[0]?.location?.id) || null;
        serviceDates.set(patientId, toLocalServiceDate(instant, resolveServiceTimeZoneInfo(location).timeZone));
    }

    return serviceDates;
};

export const fetchPatientFeeSchedule = async function (coverages) {
    const coverageArray = Array.isArray(coverages) ? coverages : [coverages];
    const validCoverages = coverageArray.filter((c) => c?.payor?.[0]?.display);
    const resultMap = new Map();

    if (!validCoverages.length) return resultMap;

    const uniqueDisplays = [...new Set(validCoverages.map((c) => c.payor[0].display))];
    const result = await processFhirBundleResponse(`BN_FeeSchedule?payers-display=${uniqueDisplays.join(',')}&active=true`);
    const allFeeSchedules = result || [];

    for (const display of uniqueDisplays) {
        const matching = allFeeSchedules.filter((fs) => fs.payers?.some((p) => p.display === display));
        resultMap.set(display, matching.length > 0 ? matching : null);
    }

    return resultMap;
};

const fetchHealthcareService = async function (billingCode) {
    let healthcareService = null;

    await $httpFhirApi.get(`HealthcareService?billingCode=${billingCode}`).then((response) => {
        // Extract the healthcare service from response
        if (response.data && response.data.entry && response.data.entry.length > 0) {
            healthcareService = new HealthcareService(response.data.entry[0].resource);
        } else if (response.data && response.data.length > 0) {
            healthcareService = new HealthcareService(response.data[0]);
        }
    });
    return healthcareService;
};

const findProcedureByBillingCode = async function (data, billingCode, healthcareServiceId = null, { allowBillingCodeFallback = false } = {}) {
    const matchesEncounter = (proc) => proc.encounter?.id === data.activeEncounter?.id || proc.context?.id === data.activeEncounter?.id;
    const matchesBillingCode = (proc) => proc.code?.coding?.some((coding) => coding.code === billingCode);
    const matchesServiceId = (proc) => proc.code?.coding?.some((coding) => coding.version === healthcareServiceId);
    const hasVersion = (proc) => proc.code?.coding?.some((coding) => coding.version);

    const pickMatch = (candidates) => {
        if (healthcareServiceId) {
            const exactMatch = candidates.find(matchesServiceId);
            if (exactMatch) return exactMatch;
            // Candidates carry a version tag belonging to other HealthcareServices.
            // The charge-master path returns null here so it never bills a sibling service's
            // procedure. The fee-schedule path opts into allowBillingCodeFallback because a
            // BN_FeeSchedule charge is keyed by billing code, not HealthcareService identity:
            // a procedure with the matching code should be priced from the payer unit schedule
            // even when its version tag points to a sibling service (#4641).
            if (candidates.some(hasVersion) && !allowBillingCodeFallback) return null;
        }
        return candidates[0];
    };

    if (data.activePatientProcedures?.length) {
        const encounterProcs = data.activePatientProcedures.filter((proc) => matchesBillingCode(proc) && matchesEncounter(proc));
        if (encounterProcs.length > 0) return pickMatch(encounterProcs);
    }

    // Fallback to API if no local match
    try {
        const procedures = await processFhirBundleResponse(`Procedure?code=${billingCode}&encounter=${data.activeEncounter.id}`);
        // Re-check the billing code locally rather than trusting the query. processFhirBundleResponse
        // wraps an empty search result as [response.data] — a Bundle, not a Procedure — so without
        // this filter a no-match search returns a truthy non-procedure and the caller's
        // `if (!relatedProcedure) continue` never fires (#5294).
        const candidates = (procedures ?? []).filter(matchesBillingCode);
        if (candidates.length) {
            const patientProcs = candidates.filter((proc) => proc.subject?.id === data.activePatient?.id);
            return pickMatch(patientProcs.length ? patientProcs : candidates);
        }
    } catch {
        // API failed, already checked local data
    }

    return null;
};

// CORE PROCESSING FUNCTIONS
const processAddOnCodes = async function ({ healthcareService, encounterDurationMinutes, threshold, references, patientId = null, claimType = null, revCode = null }, data, transaction) {
    // Always use healthcareService for add-on triggers, not the charge object
    // For group encounters, reuse the decision already made for this service instead of asking again
    let addOnCodes;
    if (shouldProcessGroupEncounter(data) && data.groupAddOnDecisions?.has(healthcareService.id)) {
        addOnCodes = data.groupAddOnDecisions.get(healthcareService.id);
    } else {
        addOnCodes = patientId
            ? await data.checkForAddOnTriggersOnce(patientId, healthcareService, encounterDurationMinutes, threshold)
            : await data.checkForAddOnTriggers(healthcareService, encounterDurationMinutes, threshold);

        // For group encounters, store the decision for reuse by all patients
        if (shouldProcessGroupEncounter(data)) {
            const serviceKey = healthcareService.id;
            data.groupAddOnDecisions.set(serviceKey, addOnCodes);
        }
    }

    // If add-on codes were confirmed by the user, process them
    if (addOnCodes && addOnCodes.length > 0) {
        const patientCoverage = data.coverageByPatient?.get(references.patient.id);

        for (const addOnCode of addOnCodes) {
            transaction = await createAndSaveChargeItem(
                {
                    chargeData: {
                        code: addOnCode,
                        display: `${healthcareService.name} - Add-on (${addOnCode})`,
                        price: healthcareService.baseCharge || 0,
                        status: 'planned',
                        dateOfService: new Date().toISOString(),
                        coverage: patientCoverage
                            ? {
                                  id: patientCoverage.id,
                                  resourceType: 'Coverage',
                                  display: patientCoverage.payor?.[0]?.display || 'Unknown Payer',
                              }
                            : undefined,
                        ...resolveClaimMeta({ claimType, revCode }, healthcareService),
                    },
                    references,
                    relatedProcedure: null,
                    isAddOn: true,
                    healthcareService,
                },
                transaction,
            );
        }
    }

    return transaction;
};

const processFeeSchedules = async function (data, patientFeeSchedules = null, existingTransaction = false) {
    let transaction = existingTransaction || [];
    let chargesProcessed = 0;

    if (!(patientFeeSchedules?.length > 0 && data.activeEncounter?.id && data.activePatient?.id)) {
        return { transaction, chargesProcessed };
    }
    // Loop through each fee schedule
    for (const feeSchedule of patientFeeSchedules) {
        if (!feeSchedule.charges || feeSchedule.charges.length === 0) {
            continue;
        }

        // Process each charge in the fee schedule
        for (const charge of feeSchedule.charges) {
            // Fetch the HealthcareService based on charge.billingCode
            if (!charge.billingCode) {
                continue;
            }

            const healthcareService = await fetchHealthcareService(charge.billingCode);
            if (!healthcareService) {
                continue;
            }

            // Only process if the encounter has a procedure with a matching billing code.
            // Fee-schedule charges are keyed by billing code, so a manually changed procedure
            // whose version tag points to a sibling service should still match here (#4641).
            const relatedProcedure = await findProcedureByBillingCode(data, healthcareService.billingCode, healthcareService.id, { allowBillingCodeFallback: true });
            if (!relatedProcedure) {
                continue;
            }

            // Create common references once to reduce repetition
            const references = createCommonReferences(data);

            // Process by healthcare service type. The schedule's billing mode decides whether the
            // schedule amount or the charge master amount is billed (#6021). A configured $0.00 is a
            // real, billable price, so this is a null check rather than a truthy check — the same rule
            // tier charges follow (#4641). An amount that was never entered (null, undefined or '')
            // still falls through and generates nothing (#5294).
            const pricingOptions = { billingMode: feeSchedule.billingMode };
            const perUnitCharge = healthcareService.fixedTimeDuration?.perUnitCharge;
            if (perUnitCharge != null && perUnitCharge !== '') {
                transaction = await processFixedTimeDuration(data, healthcareService, references, relatedProcedure, charge, transaction, pricingOptions);
                chargesProcessed++;
            } else if (healthcareService.thresholds && healthcareService.thresholds.length > 0) {
                transaction = await processThresholds(data, healthcareService, references, relatedProcedure, charge, transaction, pricingOptions);
                chargesProcessed++;
            }
        }
    }

    return { transaction, chargesProcessed };
};

// Auto-include (includeByDefault) add-on services get their own ChargeItem priced from their own
// charge master. This runs regardless of how the main charge was priced (unit schedule or charge
// master), so add-ons are never dropped when a payer fee schedule handles the main charge (#4844).
const processAutoIncludeAddOns = async function (data, services = [], existingTransaction = false) {
    let transaction = existingTransaction || [];

    if (!(services.length > 0 && data.activeEncounter?.id && data.activePatient?.id)) {
        return transaction;
    }

    for (const healthcareService of services) {
        if (!healthcareService.isAddOn) continue;

        const references = createCommonReferences(data);
        const relatedProcedure = healthcareService.billingCode ? await findProcedureByBillingCode(data, healthcareService.billingCode, healthcareService.id) : null;
        // A group participant gets an add-on only when it is on their own session record
        if (!relatedProcedure && shouldProcessGroupEncounter(data)) continue;

        const patientCoverage = data.coverageByPatient?.get(references.patient.id);

        transaction = await createAndSaveChargeItem(
            {
                chargeData: {
                    code: healthcareService.billingCode,
                    display: healthcareService.name,
                    price: getPerUnitChargeValue(healthcareService, healthcareService, 0),
                    status: 'planned',
                    dateOfService: new Date().toISOString(),
                    coverage: patientCoverage
                        ? {
                              id: patientCoverage.id,
                              resourceType: 'Coverage',
                              display: patientCoverage.payor?.[0]?.display || 'Unknown Payer',
                          }
                        : undefined,
                    ...resolveClaimMeta(null, healthcareService),
                },
                references,
                relatedProcedure,
                isAddOn: true,
                healthcareService,
            },
            transaction,
        );
    }

    return transaction;
};

// Process HealthcareService objects directly (when no fee schedule)
const processHealthcareServices = async function (data, healthcareServices = [], existingTransaction = false) {
    let transaction = existingTransaction || [];

    if (!(healthcareServices.length > 0 && data.activeEncounter?.id && data.activePatient?.id)) {
        return transaction;
    }

    // Process each healthcare service directly
    for (const healthcareService of healthcareServices) {
        // Create common references once to reduce repetition
        const references = createCommonReferences(data);

        // Add-on services get their own ChargeItem but must not enter
        // processFixedTimeDuration (which recalculates units for the encounter
        // duration and can overwrite the parent procedure's unit value).
        if (healthcareService.isAddOn) {
            transaction = await processAutoIncludeAddOns(data, [healthcareService], transaction);
            continue;
        }

        // A service with no billing code has nothing to bill under: createAndSaveChargeItem would
        // write a ChargeItem with an undefined code, which cannot be claimed and only clutters the
        // charge reports. Skip it rather than falling through to the pricing branch. Previously this
        // was masked for unpriced services because the truthy gate below dropped them anyway (#5294).
        if (!healthcareService.billingCode) continue;

        const relatedProcedure = await findProcedureByBillingCode(data, healthcareService.billingCode, healthcareService.id);
        if (!relatedProcedure) continue;

        // Process by healthcare service type. A configured $0.00 is a real, billable price, so this
        // is a null check rather than a truthy check — the same rule tier charges follow (#4641). An
        // amount that was never entered (null, undefined or '') still falls through and generates
        // nothing (#5294).
        const perUnitCharge = healthcareService?.fixedTimeDuration?.perUnitCharge;
        if (perUnitCharge != null && perUnitCharge !== '') {
            transaction = await processFixedTimeDuration(data, healthcareService, references, relatedProcedure, healthcareService, transaction);
        } else if (healthcareService.thresholds && healthcareService.thresholds.length > 0) {
            transaction = await processThresholds(data, healthcareService, references, relatedProcedure, healthcareService, transaction);
        }
    }

    return transaction;
};

const processPatientFeeSchedule = async function (data, patientId, feeSchedules, existingTransaction = false) {
    let transaction = existingTransaction || [];

    // Find the patient's encounter in participantEncounters
    const updatedEncounter = data.participantEncounters[patientId];
    if (!updatedEncounter) {
        return transaction;
    }

    // Create patient-specific data for fee schedule processing
    const patientData = {
        ...data,
        activePatient: data.patients.find((p) => p.id === patientId),
        activeEncounter: updatedEncounter,
        // Keep group encounter context for consistent processing
        isGroupEncounter: data.isGroupEncounter,
        // Set encounterDurationMinutes for single patient processing
        encounterDurationMinutes: calculateEncounterDuration(updatedEncounter),
        // Ensure checkForAddOnTriggersOnce is available
        checkForAddOnTriggersOnce: data.checkForAddOnTriggersOnce,
    };

    // Pass patientData to maintain correct patient context while preserving group encounter context
    const result = await processFeeSchedules(patientData, feeSchedules, transaction);
    transaction = result.transaction;

    return { transaction, chargesProcessed: result.chargesProcessed };
};

const processPatientHealthcareServices = async function (data, patientId, existingTransaction = false) {
    let transaction = existingTransaction || [];

    // Find the patient's encounter in participantEncounters
    const updatedEncounter = data.participantEncounters[patientId];
    if (!updatedEncounter) {
        return transaction;
    }

    // Create patient-specific data for healthcare services processing
    const patientData = {
        ...data,
        activePatient: data.patients.find((p) => p.id === patientId),
        activeEncounter: updatedEncounter,
        // Keep group encounter context for threshold processing
        isGroupEncounter: data.isGroupEncounter,
        // Set encounterDurationMinutes for single patient processing
        encounterDurationMinutes: calculateEncounterDuration(updatedEncounter),
        // Ensure checkForAddOnTriggersOnce is available
        checkForAddOnTriggersOnce: data.checkForAddOnTriggersOnce,
    };

    // Pass patientData to maintain correct patient context while preserving group encounter context
    transaction = await processHealthcareServices(patientData, data.updatedServices, transaction);

    return transaction;
};

const processPatientAutoIncludeAddOns = async function (data, patientId, existingTransaction = false) {
    let transaction = existingTransaction || [];

    // Find the patient's encounter in participantEncounters
    const updatedEncounter = data.participantEncounters[patientId];
    if (!updatedEncounter) {
        return transaction;
    }

    // Create patient-specific data for auto-include add-on processing
    const patientData = {
        ...data,
        activePatient: data.patients.find((p) => p.id === patientId),
        activeEncounter: updatedEncounter,
        // Keep group encounter context for threshold processing
        isGroupEncounter: data.isGroupEncounter,
        // Set encounterDurationMinutes for single patient processing
        encounterDurationMinutes: calculateEncounterDuration(updatedEncounter),
        // Ensure checkForAddOnTriggersOnce is available
        checkForAddOnTriggersOnce: data.checkForAddOnTriggersOnce,
    };

    // Pass patientData to maintain correct patient context while preserving group encounter context
    transaction = await processAutoIncludeAddOns(patientData, data.updatedServices, transaction);

    return transaction;
};

// Unified function to process a threshold (works for both single patient and group patient)
const createThresholdChargeItem = async function ({ threshold, healthcareService, charge, relatedProcedure, references, encounterDurationMinutes, billingMode }, data, existingTransaction = false) {
    let transaction = existingTransaction || [];

    // Prices the tier processThresholds selected. On the charge-master path the charge is the HealthcareService itself,
    // so there is no schedule charge.
    const pricing = resolveChargePricing({ healthcareService, feeScheduleCharge: charge === healthcareService ? null : charge, billingMode, durationMinutes: encounterDurationMinutes, threshold });

    // A tier with no charge at all generates nothing; a $0 tier still bills (#4641)
    if (!pricing) {
        return transaction;
    }

    const { price, expectedAmount, units, procedureCode, procedureCodeOverride } = pricing;

    // Only an explicit tier override rewrites the procedure's code
    await updateProcedureIfNeeded(relatedProcedure, procedureCodeOverride, units, transaction);

    // The coverage selected for this patient's date of service
    const patientCoverage = data.coverageByPatient?.get(references.patient.id);

    // FIRST: Create and save the main charge item
    transaction = await createAndSaveChargeItem(
        {
            chargeData: {
                code: procedureCode,
                display: charge?.name || healthcareService.name,
                price, // unit-schedule tier charge (or the charge master tier under standard-charge billing)
                expectedAmount,
                status: charge?.status || 'planned',
                dateOfService: new Date().toISOString(),
                coverage: patientCoverage
                    ? {
                          id: patientCoverage.id,
                          resourceType: 'Coverage',
                          display: patientCoverage.payor?.[0]?.display || 'Unknown Payer',
                      }
                    : undefined,
                ...resolveClaimMeta(charge, healthcareService),
            },
            references,
            relatedProcedure,
            isAddOn: false, // This is a main charge (threshold-based service)
            healthcareService,
        },
        transaction,
    );

    // AFTER creating the main charge item, check for add-on triggers
    // Always use healthcareService for add-on triggers, not the charge object
    transaction = await processAddOnCodes(
        { healthcareService, encounterDurationMinutes, threshold, references, patientId: references.patient.id, ...resolveClaimMeta(charge, healthcareService) },
        data,
        transaction,
    );

    return transaction;
};

// A group pass prices only its own participant (data.activePatient), with the procedure the pass matched on their form
// encounter, and attaches the charge to their group encounter. Returns that participant's references and group
// encounter, or null when the pass charges nothing: this lock doesn't charge the participant, or the service was already
// priced for them (a code listed in more than one of the payer's schedules is priced once per participant).
const groupParticipantChargeContext = function (data, healthcareService, references, charge) {
    const patientId = data.activePatient?.id;
    const encounter = findGroupChargeEncounter(data, patientId);
    if (!encounter) return null;

    const serviceKey = `${patientId}-${healthcareService.id}-${charge?.billingCode || healthcareService.billingCode}`;
    if (data.processedGroupServices?.has(serviceKey)) return null;
    data.processedGroupServices?.add(serviceKey);

    return { references: createPatientSpecificReferences(references, data.activePatient, encounter), encounter };
};

const processFixedTimeDuration = async function (data, healthcareService, references, relatedProcedure, charge, existingTransaction = false, options = {}) {
    let transaction = existingTransaction || [];
    const { billingMode } = options;
    // On the charge-master path the charge is the HealthcareService itself, so there is no schedule charge.
    // Units come from the patient's coverage (calculatePatientCoverageAndUnits); they agree with resolveChargePricing's
    // because a patient is only ever priced from their own coverage's schedule.
    const feeScheduleCharge = charge === healthcareService ? null : charge;

    // A group participant's duration is their form encounter's, which the pass already set as encounterDurationMinutes
    let chargeReferences = references;
    if (data.isGroupEncounter) {
        const participant = groupParticipantChargeContext(data, healthcareService, references, charge);
        if (!participant) return transaction;
        chargeReferences = participant.references;
    }

    // Use helper function to calculate coverage and units
    const { calculatedUnits, patientCoverage } = calculatePatientCoverageAndUnits(data, chargeReferences.patient.id, data.encounterDurationMinutes, charge, healthcareService);

    // Only update the procedure if calculated units differ from current units
    if (relatedProcedure?.id && calculatedUnits !== relatedProcedure.unit) {
        await updateProcedureIfNeeded(relatedProcedure, null, calculatedUnits, transaction);
    }

    // FIRST: Create and save the main charge item
    const { price, expectedAmount, procedureCode } = resolveChargePricing({ healthcareService, feeScheduleCharge, billingMode, durationMinutes: data.encounterDurationMinutes });

    transaction = await createAndSaveChargeItem(
        {
            chargeData: {
                code: procedureCode,
                display: healthcareService.name,
                price,
                expectedAmount,
                status: 'planned',
                dateOfService: new Date().toISOString(),
                coverage: patientCoverage
                    ? {
                          id: patientCoverage.id,
                          resourceType: 'Coverage',
                          display: patientCoverage.payor?.[0]?.display || 'Unknown Payer',
                      }
                    : undefined,
                ...resolveClaimMeta(charge, healthcareService),
            },
            references: chargeReferences,
            relatedProcedure,
            isAddOn: null,
            healthcareService,
        },
        transaction,
    );

    // AFTER the main charge item has been created, check for add-on triggers
    // Always use healthcareService for add-on triggers, not the charge object
    transaction = await processAddOnCodes(
        {
            healthcareService,
            encounterDurationMinutes: data.encounterDurationMinutes,
            threshold: null,
            references: chargeReferences,
            patientId: chargeReferences.patient.id,
            ...resolveClaimMeta(charge, healthcareService),
        },
        data,
        transaction,
    );

    return transaction;
};

const processThresholds = async function (data, healthcareService, references, relatedProcedure, charge, existingTransaction = false, options = {}) {
    let transaction = existingTransaction || [];
    const { billingMode } = options;

    // First prioritize charge thresholds, fall back to healthcareService thresholds
    const sourceThresholds = charge?.thresholds || healthcareService.thresholds;

    const sortedThresholds = sortThresholdsByMin(sourceThresholds);

    let chargeReferences = references;
    let encounterDurationMinutes = data.encounterDurationMinutes;
    if (data.isGroupEncounter) {
        const participant = groupParticipantChargeContext(data, healthcareService, references, charge);
        if (!participant) return transaction;
        chargeReferences = participant.references;
        // A group participant's tier is chosen by their group encounter's duration
        encounterDurationMinutes = calculateEncounterDuration(participant.encounter);
    }

    // Determine which threshold to use based on encounter duration using helper function
    let selectedThreshold = findThresholdForDuration(sortedThresholds, encounterDurationMinutes);

    // When duration is unknown (0 or unset), default to the first threshold rather than
    // creating a separate charge item for every tier under the same billing code.
    if (!selectedThreshold) {
        selectedThreshold = sortedThresholds[0];
    }

    transaction = await createThresholdChargeItem(
        { threshold: selectedThreshold, healthcareService, charge, relatedProcedure, references: chargeReferences, encounterDurationMinutes, billingMode },
        data,
        transaction,
    );

    return transaction;
};

// CHARGE ITEM CREATION FUNCTIONS
const updateProcedureIfNeeded = async function (procedure, procedureCodeOverride, units, transaction) {
    if (!procedure?.id) return;

    let needsCodeUpdate = false;
    let needsUnitUpdate = false;

    // Check if procedure code needs update
    if (procedureCodeOverride && procedureCodeOverride !== procedure.code?.coding?.[0]?.code) {
        needsCodeUpdate = true;
    }

    // Check if units need update
    if (units !== procedure.unit) {
        needsUnitUpdate = true;
    }

    // Apply updates if needed
    if (needsCodeUpdate || needsUnitUpdate) {
        const updates = {};

        // Add code update if needed
        if (needsCodeUpdate && procedure.code?.coding?.length) {
            updates.code = {
                coding: [
                    {
                        code: procedureCodeOverride,
                        display: procedure.code.coding[0].display,
                    },
                ],
            };
        }

        // Add unit update if needed
        if (needsUnitUpdate) {
            updates.unit = units;
        }

        // Perform the update
        if (Object.keys(updates).length > 0) {
            // Update local object immediately (since we're using transactions)
            if (needsCodeUpdate && procedure.code?.coding?.length) {
                procedure.code.coding[0].code = procedureCodeOverride;
            }
            if (needsUnitUpdate) {
                procedure.unit = units;
            }

            // Add procedure update to transaction
            transaction.push(crud.patch(procedure));
        }
    }
};

const createAndSaveChargeItem = async function ({ chargeData, references, relatedProcedure, isAddOn = false, healthcareService = null }, existingTransaction = false) {
    let transaction = existingTransaction || [];

    // Use the local procedure data since we're working with transactions
    // The procedure will be updated in the transaction if needed
    let latestProcedureData = relatedProcedure;

    // For add-on codes, always use 1 as the quantity
    // For regular charges, get the most accurate unit value
    const mostAccurateUnitValue = isAddOn ? 1 : latestProcedureData?.unit || relatedProcedure?.unit || chargeData.quantity;

    const readChargeItems = (existingChargeItemsResponse) => {
        if (existingChargeItemsResponse.data && existingChargeItemsResponse.data.entry && existingChargeItemsResponse.data.entry.length > 0) {
            return existingChargeItemsResponse.data.entry.map((entry) => entry.resource);
        } else if (existingChargeItemsResponse.data && Array.isArray(existingChargeItemsResponse.data)) {
            return existingChargeItemsResponse.data;
        } else if (existingChargeItemsResponse.data && existingChargeItemsResponse.data.resourceType === 'ChargeItem') {
            return [existingChargeItemsResponse.data];
        }
        return [];
    };

    // One read of the patient's charges for this session answers both matches below, so a first lock (which finds none)
    // costs no more requests than a code-only check did
    const sessionChargeItems = readChargeItems(await $httpFhirApi.get(`ChargeItem?subject=${references.patient.id}&context=${references.encounter.id}`));

    // First, a charge already billed under this code
    let existingChargeItems = sessionChargeItems.filter((ci) => ci.code?.coding?.some((coding) => coding.code === chargeData.code));

    // A charge whose code was switched on the claim (a payer change re-prices it under another code) no longer matches
    // by code, so find it by the Procedure it bills instead of creating a second charge for the same service
    let matchedByProcedure = false;
    if (!existingChargeItems.length && !isAddOn && latestProcedureData?.id) {
        existingChargeItems = sessionChargeItems.filter((ci) => ci.service?.some((service) => service.id === latestProcedureData.id));
        matchedByProcedure = existingChargeItems.length > 0;
    }

    if (existingChargeItems.length > 0) {
        // Instead of skipping, update the existing charge item with the new quantity
        const existingChargeItem = existingChargeItems[0]; // Use the first one if multiple exist

        if (matchedByProcedure && existingChargeItem.code?.coding?.[0]) {
            existingChargeItem.code.coding[0].code = chargeData.code;
        }

        // Update the quantity based on whether this is an add-on code or not
        if (existingChargeItem.quantity) {
            existingChargeItem.quantity.value = isAddOn ? 1 : mostAccurateUnitValue;
        } else {
            existingChargeItem.quantity = {
                value: isAddOn ? 1 : mostAccurateUnitValue,
                unit: 'units',
            };
        }

        // Update price — the correct tier price may have changed (e.g. encounter duration
        // was unknown when the charge item was first created)
        if (chargeData.price !== undefined && chargeData.price !== null) {
            existingChargeItem.priceOverride = {
                value: chargeData.price,
                currency: 'USD',
            };
        }
        // Expected (contracted) amount only exists under standard-charge billing; clear a stale
        // one when the schedule no longer bills that way (#6021). Aidbox rejects null — delete.
        if (chargeData.expectedAmount != null) {
            existingChargeItem.expectedAmount = {
                value: chargeData.expectedAmount,
                currency: 'USD',
            };
        } else {
            delete existingChargeItem.expectedAmount;
        }
        // Update claimType and revCode if present in chargeData
        if (chargeData.claimType !== undefined) {
            existingChargeItem.claimType = chargeData.claimType;
        }
        if (chargeData.revCode !== undefined) {
            existingChargeItem.revCode = chargeData.revCode;
        }

        // If procedure has updated service reference, update it in charge item too
        if (latestProcedureData?.id) {
            existingChargeItem.service = [
                {
                    id: latestProcedureData.id,
                    resourceType: 'Procedure',
                },
            ];
        }

        let { getChargeItemAuths } = useAuthFlow();
        await getChargeItemAuths(references.patient, healthcareService, latestProcedureData, existingChargeItem);

        // Update the charge item in the database
        transaction.push(crud.put(existingChargeItem));
        return transaction;
    }

    // Create the charge item using our class
    const chargeItemData = {
        status: chargeData.status || 'planned',
        code: {
            coding: [
                {
                    code: chargeData.code,
                    display: chargeData.display,
                },
            ],
        },
        subject: references.patient,
        context: references.encounter,
        performer: [
            {
                actor: references.provider,
            },
        ],
        quantity: {
            value: isAddOn ? 1 : mostAccurateUnitValue,
            unit: 'units',
        },
        priceOverride: {
            value: chargeData.price,
            currency: 'USD',
        },
        coverage: chargeData.coverage,
    };

    if (chargeData.claimType) {
        chargeItemData.claimType = chargeData.claimType;
    }
    if (chargeData.revCode) {
        chargeItemData.revCode = chargeData.revCode;
    }
    if (chargeData.expectedAmount != null) {
        chargeItemData.expectedAmount = {
            value: chargeData.expectedAmount,
            currency: 'USD',
        };
    }

    const chargeItem = new ChargeItem(chargeItemData);

    // Set occurrence[x] using ValueX polymorphic setter
    chargeItem.occurrenceX = chargeData.dateOfService || new Date().toISOString();

    // Add reference to Procedure if available
    if (latestProcedureData?.id) {
        chargeItem.service = [
            new Reference({
                id: latestProcedureData.id,
                resourceType: 'Procedure',
            }),
        ];

        // Ensure the chargeItem code matches the procedure
        if (latestProcedureData.code?.coding?.length) {
            chargeItem.code = {
                coding: [
                    {
                        code: latestProcedureData.code.coding[0].code,
                        display: latestProcedureData.code.coding[0].display,
                    },
                ],
            };
        }
    }

    // Ensure the chargeItem quantity matches the procedure (regardless of add-on status)
    if (latestProcedureData?.unit) {
        chargeItem.quantity = {
            value: latestProcedureData.unit,
            unit: 'units',
        };
    }

    if (references.questionnaireResponse) {
        chargeItem.supportingInformation = [references.questionnaireResponse];
    } else {
        delete chargeItem.supportingInformation;
    }

    // Save the charge item - convert to JSON and set ID
    const chargeItemJson = chargeItem.toJSON();
    const chargeItemId = uuidv4();
    chargeItemJson.id = chargeItemId;

    // Create a plain object with resourceType and toJSON method for crud.post
    const chargeItemForCrud = {
        resourceType: 'ChargeItem',
        toJSON: () => chargeItemJson,
    };

    // Add to transaction instead of direct API call
    transaction.push(crud.post(chargeItemForCrud));

    let { getChargeItemAuths } = useAuthFlow();
    let patients = [references.patient];
    let services = [healthcareService];
    let procedures = [latestProcedureData];

    transaction = await getChargeItemAuths(patients, services, procedures, chargeItemJson, true, transaction);

    return transaction;
};

// DECISION LOGIC FUNCTIONS
const shouldProcessGroupEncounter = function (data) {
    return data.isGroupEncounter && data.participantEncounters && Object.keys(data.participantEncounters).length > 0;
};

const shouldProcessWithCoverageFeeSchedule = async function (data, patientIds) {
    const ids = Array.isArray(patientIds) ? patientIds : [patientIds];
    const defaultResult = { shouldProcess: false, coverage: null, feeSchedule: null };
    const results = new Map();

    const validIds = ids.filter(Boolean);
    if (!validIds.length) {
        validIds.forEach((id) => results.set(id, defaultResult));
        return results;
    }

    const serviceDateByPatient = await fetchServiceDates(data, validIds);
    const coverageMap = await fetchPatientCoverage(validIds, serviceDateByPatient);
    // Charge generation's only coverage lookup (authorization deduction, getChargeItemAuths, reads its own): every
    // ChargeItem the lock creates for a patient, add-ons included, reads this patient's coverage from here, so the
    // charge's coverage and its pricing agree
    data.coverageByPatient = coverageMap;

    const allCoverages = validIds.map((id) => coverageMap.get(id)).filter(Boolean);
    // This has to be done after fetchPatientCoverage cause there is no link between the coverage and the fee schedule
    const feeScheduleMap = await fetchPatientFeeSchedule(allCoverages);

    validIds.forEach((id) => {
        const coverage = coverageMap.get(id);
        const feeSchedule = coverage?.payor?.[0]?.display ? feeScheduleMap.get(coverage.payor[0].display) : null;
        const shouldProcess = feeSchedule && feeSchedule.length > 0;
        results.set(id, { shouldProcess, coverage, feeSchedule });
    });

    return results;
};
// The encounter a group participant's charges attach to (their groupEncounters entry), or null when the lock doesn't
// charge them. A participant is charged only with both a participant encounter and a group encounter; on a patient
// page groupEncounters holds only the route patient.
const findGroupChargeEncounter = function (data, patientId) {
    if (!data.participantEncounters?.[patientId]) return null;
    return data.groupEncounters?.find((enc) => enc.subject?.id === patientId) || null;
};

const calculatePatientCoverageAndUnits = function (data, patientId, encounterDurationMinutes, charge, healthcareService) {
    const patientCoverage = data.coverageByPatient?.get(patientId);
    const hasCoverage = !!patientCoverage;
    // Get per unit duration and max units based on coverage
    const { perUnitDuration, maxUnitAllowed } = getPerUnitDurationAndMaxUnits(hasCoverage, charge, healthcareService);
    // Calculate units based on duration
    const calculatedUnits = calculateUnitsForDuration(encounterDurationMinutes, perUnitDuration, maxUnitAllowed);

    return { patientCoverage, hasCoverage, perUnitDuration, maxUnitAllowed, calculatedUnits };
};

// MAIN FUNCTION
const processChargeItemFlow = async function (data, existingTransaction = false) {
    let transaction = existingTransaction || [];

    // Validate required parameters (following codebase pattern)
    if (!data?.actor || !data?.activeEncounter) {
        return transaction;
    }

    // Track which patients have already had add-on triggers checked to prevent duplicates
    const addOnTriggersChecked = new Set();
    // Track which services have already been processed in group encounters to prevent duplicates
    const processedGroupServices = new Set();

    // Add the wrapper function to the data object for easy access throughout the call chain
    data.checkForAddOnTriggersOnce = async (patientId, service, durationMinutes, selectedThreshold) => {
        const key = `${patientId}-${service.id}`;
        if (addOnTriggersChecked.has(key)) {
            return null;
        }

        addOnTriggersChecked.add(key);
        return await data.checkForAddOnTriggers(service, durationMinutes, selectedThreshold);
    };
    // Add group service tracking to data object
    data.processedGroupServices = processedGroupServices;
    // The group's answer to each service's suggested add-on prompt. Created here so every participant's pass, which
    // works on its own copy of data, shares it.
    data.groupAddOnDecisions = new Map();

    // Use decision functions to determine processing approach
    if (shouldProcessGroupEncounter(data)) {
        const uniquePatientIds = [...new Set(Object.keys(data.participantEncounters))];
        const validPatientIds = uniquePatientIds.filter((id) => data.patients.find((p) => p.id === id) && findGroupChargeEncounter(data, id));
        const coverageMap = await shouldProcessWithCoverageFeeSchedule(data, validPatientIds);

        for (const patientId of validPatientIds) {
            const { shouldProcess: shouldProcessWithFeeSchedule, feeSchedule: patientFeeSchedule } = coverageMap.get(patientId) || {};

            if (shouldProcessWithFeeSchedule && patientFeeSchedule) {
                // Process using the patient's fee schedule
                const result = await processPatientFeeSchedule(data, patientId, patientFeeSchedule, transaction);
                transaction = result.transaction;

                // Only fall back to healthcare services if fee schedule actually created no charge items
                // AND we have updatedServices to process
                if (result.chargesProcessed === 0 && data.updatedServices?.length > 0) {
                    transaction = await processPatientHealthcareServices(data, patientId, transaction);
                } else if (result.chargesProcessed > 0 && data.updatedServices?.some((s) => s.isAddOn)) {
                    // Fee schedule priced the main charge (processPatientHealthcareServices skipped) — still
                    // create this participant's auto-include add-on ChargeItems (#4844).
                    transaction = await processPatientAutoIncludeAddOns(data, patientId, transaction);
                }
            } else {
                // No fee schedule but has coverage, or no coverage - use healthcare services
                transaction = await processPatientHealthcareServices(data, patientId, transaction);
            }
        }
    } else {
        // For single patient encounters, use the original approach
        const patientId = data.activePatient?.id;
        let patientProcessed = false;

        if (patientId) {
            // Use decision function to check if patient has coverage with fee schedule
            const coverageMap = await shouldProcessWithCoverageFeeSchedule(data, patientId);
            const { shouldProcess: shouldProcessWithFeeSchedule, feeSchedule: coverageFeeSchedule } = coverageMap.get(patientId) || {};

            if (shouldProcessWithFeeSchedule && coverageFeeSchedule) {
                // Process using the coverage's feeSchedule instead of healthcareService
                const result = await processFeeSchedules(data, coverageFeeSchedule, transaction);
                transaction = result.transaction;

                // Only mark as processed if fee schedule actually created charge items
                if (result.chargesProcessed > 0) {
                    patientProcessed = true;
                }
            }

            // Only process through healthcareService if patient hasn't been processed through the coverage's feeSchedule.
            // The session form's data.feeScheduleData is deliberately not a fallback: it was loaded without regard to the
            // date of service, so it can belong to a payer that didn't cover the session.
            if (!patientProcessed && data.updatedServices?.length > 0) {
                // Fall back to healthcare services
                transaction = await processHealthcareServices(data, data.updatedServices, transaction);
            }

            // Auto-include add-ons must still be created when a fee/unit schedule priced the main charge
            // (which skips processHealthcareServices above). The charge-master path already created them
            // inside processHealthcareServices, so only run here when that path was skipped (#4844).
            if (patientProcessed && data.updatedServices?.some((s) => s.isAddOn)) {
                transaction = await processAutoIncludeAddOns(data, data.updatedServices, transaction);
            }
        }
    }

    // Execute the transaction if this is not an existing transaction
    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
};

const removeFormChargeItems = async function (chargeItemIds, existingTransaction = false) {
    let transaction = existingTransaction || [];

    // Delete each ChargeItem
    for (const chargeItemId of chargeItemIds) {
        transaction.push(crud.delete({ id: chargeItemId, resourceType: 'ChargeItem' }));
    }

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
};

/**
 * Given a HealthcareService and the encounter duration in minutes, return the
 * price and billing code that should be used for charge creation.
 *
 * Mirrors the logic in processThresholds / createThresholdChargeItem so that
 * callers (e.g. the per-diem report) that defer charge creation don't have to
 * re-implement threshold selection themselves.
 *
 * @param {Object} hs - HealthcareService resource
 * @param {number} durationMinutes - encounter duration in minutes
 * @returns {{ price: number|undefined, procedureCode: string|undefined, units: number|undefined }}
 */
const resolveServicePricing = function (hs, durationMinutes) {
    if (!hs) return { price: undefined, procedureCode: undefined, units: undefined };

    const billingCode = hs.billingCode;

    // Per service bills one unit at its flat amount, whatever the duration.
    if (hs.uomType === 'Service') {
        const rawCharge = hs.fixedTimeDuration?.perUnitCharge;
        return { price: rawCharge != null && rawCharge !== '' ? Number(rawCharge) : undefined, procedureCode: billingCode, units: 1 };
    }

    if (hs.thresholds?.length) {
        const matched = findThresholdForDuration(sortThresholdsByMin(hs.thresholds), durationMinutes);
        if (matched) {
            // Mirror createThresholdChargeItem: the matched tier carries both the per-unit
            // charge and the unit count. Callers that defer charge creation (per-diem report)
            // need the units too, or they would bill the right rate at the wrong quantity.
            return {
                price: matched.charge != null ? Number(matched.charge) : undefined,
                procedureCode: matched.procedureCodeOverride || billingCode,
                units: matched.units != null ? Number(matched.units) : undefined,
            };
        }
    }

    const rawCharge = hs.fixedTimeDuration?.perUnitCharge ?? hs.baseCharge;
    const perUnitDuration = hs.fixedTimeDuration?.perUnitDuration;
    const units = perUnitDuration ? calculateUnitsForDuration(durationMinutes, perUnitDuration, hs.fixedTimeDuration?.maxUnitsAllowed) : undefined;
    return {
        price: rawCharge != null ? Number(rawCharge) : undefined,
        procedureCode: billingCode,
        units,
    };
};

/**
 * The price, expected amount, units and procedure code a charge for this service gets, from the charge master alone or
 * with a payer's fee schedule charge on top. Lock-time charge generation and the claim edit dialog's re-pricing on a
 * payer change both price through this, so the two can't disagree. Makes no requests. A caller that has already
 * selected the tier for this duration passes it as `threshold`; otherwise it's selected here.
 *
 * @returns {{ price: number, expectedAmount: number|undefined, units: number, procedureCode: string, procedureCodeOverride: string|undefined }|null}
 * null when the service has no price for this duration. procedureCodeOverride is set only by an explicit tier override.
 */
export function resolveChargePricing({ healthcareService, feeScheduleCharge = null, billingMode, durationMinutes, threshold: selectedThreshold = null }) {
    // The charge master's shape picks the branch, as at lock (processFeeSchedules / processHealthcareServices)
    const perUnitCharge = healthcareService?.fixedTimeDuration?.perUnitCharge;
    let contractedAmount;
    let standardAmount;
    let units;
    let procedureCode;
    let procedureCodeOverride;

    if (perUnitCharge != null && perUnitCharge !== '') {
        // Duration and max units come from the schedule charge whenever one is given, with no per-field fallback
        const { perUnitDuration, maxUnitAllowed } = getPerUnitDurationAndMaxUnits(!!feeScheduleCharge, feeScheduleCharge, healthcareService);
        contractedAmount = getPerUnitChargeValue(feeScheduleCharge, healthcareService, 0);
        standardAmount = getPerUnitChargeValue(null, healthcareService, 0);
        units = calculateUnitsForDuration(durationMinutes, perUnitDuration, maxUnitAllowed);
        procedureCode = healthcareService.billingCode;
    } else if (healthcareService?.thresholds?.length) {
        const sortedThresholds = [...(feeScheduleCharge?.thresholds || healthcareService.thresholds)].sort((a, b) => (a.min || 0) - (b.min || 0));
        // An unknown duration (0 or unset) bills the first tier
        const threshold = selectedThreshold || findThresholdForDuration(sortedThresholds, durationMinutes) || sortedThresholds[0];
        if (!threshold) return null;

        // A schedule tier with no charge of its own falls back to the charge master tier with the same bounds (#4641)
        const healthcareServiceThreshold = healthcareService.thresholds.find((t) => (t.min ?? -1) === (threshold.min ?? -1) && (t.max ?? Infinity) === (threshold.max ?? Infinity));
        contractedAmount = threshold.charge ?? healthcareServiceThreshold?.charge;
        // A tier charge of 0 is a real $0 tier, so only a missing charge leaves the service unpriced (#4641)
        if (contractedAmount == null) return null;

        standardAmount = resolveServicePricing(healthcareService, durationMinutes).price;
        units = threshold.units || healthcareServiceThreshold?.units || 1;
        procedureCodeOverride = threshold.procedureCodeOverride || healthcareServiceThreshold?.procedureCodeOverride;
        procedureCode = procedureCodeOverride || feeScheduleCharge?.billingCode || healthcareService.billingCode;
    } else {
        return null;
    }

    const { price, expectedAmount } = feeScheduleCharge ? resolveBilledAmounts({ billingMode, contractedAmount, standardAmount }) : { price: contractedAmount, expectedAmount: undefined };

    return { price, expectedAmount, units, procedureCode, procedureCodeOverride };
}

export function useChargeItemFlow() {
    return {
        processChargeItemFlow,
        removeFormChargeItems,
        fetchPatientCoverage,
        resolveServicePricing,
    };
}
